import type { ApiErrorBody, ApiErrorCode } from '@rm/shared';

/**
 * A stable id for this browser tab.
 *
 * Sent on every mutation and echoed back on the matching realtime event, so a
 * tab can ignore the broadcast of its own change. Without it, your optimistic
 * update gets re-applied from the server a moment later and the card visibly
 * flickers.
 */
export const clientId = (() => {
  const KEY = 'rm:clientId';
  try {
    const existing = sessionStorage.getItem(KEY);
    if (existing) return existing;
    const fresh = crypto.randomUUID();
    sessionStorage.setItem(KEY, fresh);
    return fresh;
  } catch {
    // Private mode, or storage blocked. A per-load id still works.
    return crypto.randomUUID();
  }
})();

export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly status: number;
  readonly issues?: Array<{ path: string; message: string }>;
  /** Server truth, present on 409 so the caller can reconcile. */
  readonly current?: unknown;
  readonly requestId?: string;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.error;
    if (body.issues) this.issues = body.issues;
    if (body.current !== undefined) this.current = body.current;
    if (body.requestId) this.requestId = body.requestId;
  }

  get isConflict(): boolean {
    return this.code === 'conflict' || this.code === 'stale_write';
  }
}

function readCookie(name: string): string | null {
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}=([^;]*)`),
  );
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

/**
 * The CSRF cookie is set by the server when it serves a page, and rotated on
 * login. Its name carries the `__Host-` prefix in production, where the app is
 * always on HTTPS.
 */
function csrfToken(): string | null {
  return readCookie('__Host-rm_csrf') ?? readCookie('rm_csrf');
}

/**
 * Guarantees this browser holds a CSRF token, fetching one if it does not.
 *
 * The cookie normally arrives with the HTML, but that only happens when Express
 * serves the page. In development Vite serves it and proxies just `/api`, so a
 * visitor can reach the login form having never received a cookie — and every
 * sign-in attempt would fail with "missing CSRF token" until they did.
 *
 * Concurrent callers share one in-flight request rather than racing to mint
 * competing tokens, and the result is deliberately not cached beyond that: the
 * cookie is the source of truth, and it is rotated on login.
 */
let tokenRequest: Promise<string | null> | null = null;

async function ensureCsrfToken(): Promise<string | null> {
  const existing = csrfToken();
  if (existing) return existing;

  tokenRequest ??= fetch('/api/csrf', {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  })
    .then((response) => (response.ok ? (response.json() as Promise<{ token: string }>) : null))
    .then((body) => body?.token ?? csrfToken())
    .catch(() => null)
    .finally(() => {
      tokenRequest = null;
    });

  return tokenRequest;
}

const SAFE = new Set(['GET', 'HEAD']);

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /**
   * Suppresses the automatic bounce to /login on a 401.
   *
   * Used by the boot-time session probe, where a 401 is the *expected* answer
   * for a signed-out visitor rather than a session that just expired.
   */
  allowUnauthenticated?: boolean;
}

/** Pages where a 401 is normal and must never trigger a redirect. */
const PUBLIC_PATHS = [/^\/login\/?$/, /^\/invite\//];

function onPublicPage(): boolean {
  return PUBLIC_PATHS.some((re) => re.test(window.location.pathname));
}

/**
 * Sends the caller to the login page, preserving where they were headed.
 *
 * Reached when a session expires while the app is open. A hard navigation
 * rather than a router push, so the server re-issues a fresh CSRF cookie on
 * the way in.
 *
 * It must no-op on the login page itself: the app probes `/api/me` at boot, and
 * for a signed-out visitor that 401 would otherwise redirect /login to /login,
 * forever.
 */
function redirectToLogin(): void {
  if (onPublicPage()) return;

  const here = window.location.pathname + window.location.search;
  const next = here && here !== '/' ? `?next=${encodeURIComponent(here)}` : '';
  window.location.replace(`/login${next}`);
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const unsafe = !SAFE.has(method);

  const send = async (token: string | null): Promise<Response> => {
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (unsafe) {
      headers['Content-Type'] = 'application/json';
      headers['X-Client-Id'] = clientId;
      if (token) headers['X-CSRF-Token'] = token;
    }

    return fetch(path, {
      method,
      headers,
      credentials: 'same-origin',
      ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  };

  let response = await send(unsafe ? await ensureCsrfToken() : null);

  /*
   * One retry, only for a CSRF rejection, and only once. The token can be
   * legitimately stale — it is rotated on login, and a second tab may have
   * rotated it out from under this one — and re-fetching is the whole remedy.
   * The server uses a distinct `csrf` code precisely so this cannot swallow a
   * real 403 by retrying a permission denial.
   */
  if (unsafe && response.status === 403) {
    const body = (await response.clone().json().catch(() => null)) as ApiErrorBody | null;
    if (body?.error === 'csrf') {
      const fresh = await fetch('/api/csrf', {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      })
        .then((r) => (r.ok ? (r.json() as Promise<{ token: string }>) : null))
        .then((b) => b?.token ?? null)
        .catch(() => null);

      if (fresh) response = await send(fresh);
    }
  }

  if (response.status === 204) return undefined as T;

  const isJson = response.headers.get('content-type')?.includes('application/json');

  if (!response.ok) {
    const body: ApiErrorBody = isJson
      ? await response.json()
      : { error: 'internal', message: response.statusText };

    // The API answers an expired session with 401 rather than a redirect —
    // precisely so this branch can tell the difference and act on it.
    if (
      response.status === 401 &&
      !options.allowUnauthenticated &&
      !path.startsWith('/api/auth/')
    ) {
      redirectToLogin();
    }

    throw new ApiError(response.status, body);
  }

  return isJson ? ((await response.json()) as T) : (undefined as T);
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) =>
    apiFetch<T>(path, signal ? { signal } : {}),
  post: <T>(path: string, body?: unknown) => apiFetch<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => apiFetch<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown) => apiFetch<T>(path, { method: 'PUT', body }),
  delete: <T>(path: string) => apiFetch<T>(path, { method: 'DELETE' }),
};
